import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ExportField, ExportSection, SelectedExportField } from '../maintenance-dashboard-interfaces/maintenance-dashboard.interface';
import { EXPORT_SECTIONS, EXPORT_STATUS_OPTIONS, OFFBOARDING_FILTER, exportFieldId } from './export-field-catalog';
import { ExportFileService } from './export-file-services/export-file.service';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { Router } from '@angular/router';
import { ExportFileModel } from './export-file-models/export-file.models';
import { ExportFileColumns } from './export-file-interface/export-file.interaface';
import { HttpStatusCode } from 'axios';

/** A field paired with its selection id, for the checkbox list. */
interface FieldRow {
  id: string;
  field: ExportField;
}

/** A section resolved for the accordion — its fields narrowed by the search box. */
interface SectionRow {
  section: ExportSection;
  /** Fields to render in this panel; the whole section unless a search narrows it. */
  rows: FieldRow[];
  /** Selected fields in the whole section, so a search never understates the badge. */
  selectedCount: number;
}

/**
 * Export set-up for the adviser list: a rep-status filter and a per-section
 * column picker, seeded from the manager's last export. Confirming downloads the
 * workbook from the server-side export endpoint and closes the dialog.
 */
@Component({
  selector: 'app-export-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatTooltipModule
  ],
  templateUrl: './export-dialog.component.html',
  styleUrl: './export-dialog.component.scss'
})
export class ExportDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ExportDialogComponent>);
  private exportExcelService = inject(ExportFileService);
  private snackBarService = inject(SnackbarService);
  private router = inject(Router);
  setSprinner = signal(false);

  readonly statusOptions = EXPORT_STATUS_OPTIONS;
  readonly sections: ExportSection[] = EXPORT_SECTIONS;

  /**
   * Every field in the catalog by its selection id, for resolving selections back
   * to the columns the export payload carries. Declared before the signals that
   * read it — class fields initialise in declaration order.
   */
  private readonly fieldIndex = new Map<string, SelectedExportField>(
    EXPORT_SECTIONS.flatMap((section) =>
      section.fields.map((field) => [
        exportFieldId(section.id, field.label),
        {
          sectionId: section.id,
          sectionTitle: section.title,
          field,
          id: exportFieldId(section.id, field.label)
        } satisfies SelectedExportField
      ] as const)
    )
  );

  /** Rep-status filter; defaults to "All Status". */
  readonly selectedStatus = signal('');

  /**
   * `sectionId::fieldLabel` ids — the chosen columns. Seeded from the manager's
   * last export so the tedious part of the set-up is remembered; the rep-status
   * filter deliberately is not, because a silently pre-applied filter would let
   * someone export a narrower list than they meant to.
   */
  selectedIds = signal<string[]>([]);

  /** The seeded selection, so "Reset" can restore it after the user edits away. */
  private readonly seededIds = signal<string[]>([]);

  /** True once a previous export's columns have been restored into the picker. */
  readonly hasSeed = computed(() => this.seededIds().length > 0);

  /** The one expanded panel — opening a section collapses the previous one. */
  private readonly openSectionId = signal<string | null>(EXPORT_SECTIONS[0].id);

  /** Filters the fields of every section. */
  readonly fieldSearch = signal('');

  /** Panel open before a search took over the accordion, restored when it clears. */
  private preSearchOpen: string | null = null;

  /**
   * Every section with its fields narrowed by the search box. A section left with
   * no match drops out of the accordion while a search is running.
   */
  readonly sectionRows = computed<SectionRow[]>(() => {
    const query = this.fieldSearch().trim().toLowerCase();
    const chosen = new Set(this.selectedIds());
    return this.sections
      .map((section) => ({
        section,
        rows: section.fields
          .filter((field) => !query || field.label.toLowerCase().includes(query))
          .map((field) => ({ id: exportFieldId(section.id, field.label), field })),
        selectedCount: section.fields.filter((field) =>
          chosen.has(exportFieldId(section.id, field.label))
        ).length
      }))
      .filter((row) => !query || row.rows.length > 0);
  });

  /**
   * Selection resolved to catalog order rather than click order, so the sheet
   * reads section by section however the user assembled it.
   */
  readonly selectedFields = computed<SelectedExportField[]>(() => {
    const chosen = new Set(this.selectedIds());
    return [...this.fieldIndex.values()].filter((entry) => chosen.has(entry.id));
  });

  /** Selected columns grouped by section, for the summary strip. */
  readonly selectionBySection = computed(() => {
    const groups = new Map<string, { title: string; fields: SelectedExportField[] }>();
    for (const entry of this.selectedFields()) {
      const group = groups.get(entry.sectionId) ?? { title: entry.sectionTitle, fields: [] };
      group.fields.push(entry);
      groups.set(entry.sectionId, group);
    }
    return [...groups.values()];
  });

  /** Nothing to export until at least one column is chosen. */
  readonly canExport = computed(() => this.selectedFields().length > 0 && !this.setSprinner());

  async ngOnInit(): Promise<void> {
    await this.seedFromHistory();
  }

  // ── History seeding ──────────────────────────────────────────
  /**
   * Pre-ticks the columns of the most recent export. Best-effort by design: the
   * service swallows its own failures, and an entry naming columns this build no
   * longer offers simply contributes the ones it still can.
   */
  private async seedFromHistory(): Promise<void> {
    const history = await this.exportExcelService.getExcelFieldshistory();
    const columns = history[0]?.columns;
    if (!columns?.length) {
      return;
    }

    const ids = columns
      .map((column) => this.idForColumn(column))
      .filter((id): id is string => !!id);

    if (!ids.length) {
      return;
    }

    this.seededIds.set(ids);
    this.selectedIds.set(ids);
    // Open the first section the seed spans, so the restored ticks are visible
    // rather than hidden behind a panel the user has to go looking for.
    this.openSectionId.set(this.firstSectionOf(ids) ?? EXPORT_SECTIONS[0].id);
  }

  /**
   * Resolves a saved column back to a selection id. Matches on the record's field
   * key first — a label can be reworded between releases, the key should not be —
   * and falls back to the label so entries saved before a key was stored still
   * resolve.
   */
  private idForColumn(column: Partial<ExportFileColumns>): string | null {
    if (column.field) {
      for (const entry of this.fieldIndex.values()) {
        if (entry.field.field === column.field) {
          return entry.id;
        }
      }
    }
    if (column.label) {
      for (const entry of this.fieldIndex.values()) {
        if (entry.field.label === column.label) {
          return entry.id;
        }
      }
    }
    return null;
  }

  // ── Status ───────────────────────────────────────────────────
  protected selectStatus(value: string): void {
    this.selectedStatus.set(value);
  }

  // ── Accordion ────────────────────────────────────────────────
  protected isOpen(sectionId: string): boolean {
    return this.openSectionId() === sectionId;
  }

  /**
   * Mirrors a panel's own expand/collapse back into state. Only one section is
   * open at a time, so opening one is enough to close whichever was.
   */
  protected setOpen(sectionId: string, open: boolean): void {
    if (open) {
      this.openSectionId.set(sectionId);
    } else if (this.openSectionId() === sectionId) {
      this.openSectionId.set(null);
    }
  }

  /**
   * A match hidden behind a closed panel would read as "no results", so a search
   * opens the first section that has one, and clearing it restores what was open.
   */
  protected onSearchChange(value: string): void {
    const wasSearching = !!this.fieldSearch().trim();
    this.fieldSearch.set(value);

    if (value.trim()) {
      if (!wasSearching) {
        this.preSearchOpen = this.openSectionId();
      }
      this.openSectionId.set(this.sectionRows()[0]?.section.id ?? null);
    } else if (wasSearching) {
      this.openSectionId.set(this.preSearchOpen);
      this.preSearchOpen = null;
    }
  }

  // ── Field selection ──────────────────────────────────────────
  protected isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  protected toggleField(id: string, checked: boolean): void {
    this.selectedIds.update((ids) =>
      checked ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((item) => item !== id)
    );
  }

  /** Adds every field the panel is showing — the search's matches while one runs. */
  protected selectAllIn(row: SectionRow): void {
    const visible = row.rows.map((field) => field.id);
    this.selectedIds.update((ids) => [...ids, ...visible.filter((id) => !ids.includes(id))]);
  }

  /** Removes every field the panel is showing. */
  protected clearIn(row: SectionRow): void {
    const visible = new Set(row.rows.map((field) => field.id));
    this.selectedIds.update((ids) => ids.filter((id) => !visible.has(id)));
  }

  protected removeSelected(id: string): void {
    this.selectedIds.update((ids) => ids.filter((item) => item !== id));
  }

  protected clearSelection(): void {
    this.selectedIds.set([]);
  }

  /** Restores the columns the dialog opened with — the last export's set. */
  protected resetToSeed(): void {
    this.selectedIds.set([...this.seededIds()]);
  }

  /** The first section, in catalog order, that a set of selection ids spans. */
  private firstSectionOf(fieldIds: string[]): string | null {
    const spanned = new Set(
      fieldIds.map((id) => this.fieldIndex.get(id)?.sectionId).filter((id): id is string => !!id)
    );
    return this.sections.find((section) => spanned.has(section.id))?.id ?? null;
  }

  // ── Dialog actions ───────────────────────────────────────────
  protected cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Downloads the workbook, then records the column set so the next export opens
   * pre-ticked. The download is the operation that matters: its failure aborts
   * with the dialog left open and the selection intact, and history is only
   * written once the file is actually in hand.
   */
  protected async confirm(): Promise<void> {
    const fields = this.selectedFields();
    if (!fields.length) {
      this.snackBarService.openWarningSnackBar('Select at least one field to export.');
      return;
    }

    const exportExcelModel = new ExportFileModel();
    exportExcelModel.setColumns(this.toExportColumns(fields));

    // "Off-boarding Initiated" is a stage rather than a rep status — it spans
    // Inforced and Serving Notice — so it travels as its own flag, never as a
    // status string the backend would fail to match.
    const status = this.selectedStatus();
    exportExcelModel.setStatus(status === OFFBOARDING_FILTER ? '' : status);
    exportExcelModel.setisOffboarded(status === OFFBOARDING_FILTER);

    this.setSprinner.set(true);
    try {
      await this.exportExcelService.exportExcel(exportExcelModel);
    } catch (error: any) {
      this.setSprinner.set(false);
      this.handleFailure(error);
      return;
    }

    // Remembering the column set is a convenience — a failure here must not turn
    // a download the user already has into an error.
    try {
      await this.exportExcelService.saveExcelFieldshistory(exportExcelModel);
    } catch {
      // Intentionally ignored.
    }

    this.setSprinner.set(false);
    this.snackBarService.openSuccessSnackBar('Excel file downloaded successfully!');
    this.dialogRef.close();
  }


  protected async saveSelection(): Promise<void> {
    const fields = this.selectedFields();
    const exportExcelModel = new ExportFileModel();
    exportExcelModel.setColumns(this.toExportColumns(fields));

    this.setSprinner.set(true);
    try {
      await this.exportExcelService.saveExcelFieldshistory(exportExcelModel);
    } catch (error: any) {
      this.setSprinner.set(false);
      this.handleFailure(error);
      return;
    }

    this.setSprinner.set(false);
    this.snackBarService.openSuccessSnackBar('Excel fields saved successfully!');

  }

  /** The selection as the export payload's `columns`, in catalog order. */
  private toExportColumns(fields: SelectedExportField[]): ExportFileColumns[] {
    return fields.map((entry) => ({
      section: entry.field.section,
      label: entry.field.label,
      field: entry.field.field
    }));
  }

  /** Session timeouts return the user to login; everything else is a retry prompt. */
  private handleFailure(error: any): void {
    const status = Number(error?.code ?? error?.status);

    if (status === HttpStatusCode.Unauthorized) {
      this.snackBarService.openFailureSnackBar('Session timed out. Try logging in again!');
      this.dialogRef.close();
      this.router.navigate(['/login']);
      return;
    }

    this.snackBarService.openFailureSnackBar(
      'We couldn’t process your request. Please try again'
    );
  }
}
